import type { Currency } from "@freightsimple/generated-dashboard-openapi-client/models/Currency";
import type { Invoice } from "@freightsimple/generated-dashboard-openapi-client/models/Invoice";
import type { Shipment } from "@freightsimple/generated-dashboard-openapi-client/models/Shipment";
import { InvoiceStanding } from "@freightsimple/generated-dashboard-openapi-client/models/InvoiceStanding";
import { InvoiceWarningState } from "@freightsimple/generated-dashboard-openapi-client/models/InvoiceWarningState";
import { asLocalDate } from "@freightsimple/time/branded-types";
import { formatLocalDate } from "@freightsimple/time/helpers";
import { Banner } from "@freightsimple/ui-core/Display/Banner";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { useState } from "react";
import { PaymentsMakePaymentsDialog } from "@/Features/Payments/Exports/Dialogs/PaymentsMakePaymentsDialog";
import { formatSignedPrice } from "@/Helpers/formatSignedPrice";
import {
  invoicesCreditBannerTitle,
  invoicesGenerateCreditBannerMessage,
} from "../Helpers/invoicesGenerateCreditBannerMessage";
import { invoicesSingleCurrency } from "../Helpers/invoicesSingleCurrency";

interface CreditBannerProps {
  shipments: Shipment[];
  invoices: Invoice[];
  paymentTermsDays: number | undefined;
  creditAmount: number | undefined;
  creditCurrency: Currency | undefined;
  issuedInvoicesTotal: number | undefined;
  /**
   * Open charges with refunds excluded, not subtracted. Only the credit-limit suspension banner
   * uses it, because that is the figure the suspension rule measured.
   */
  issuedInvoicesChargesTotal: number | undefined;
  overdueIssuedInvoicesTotal: number | undefined;
  invoiceWarningState: InvoiceWarningState;
  /**
   * Whether booking is actually blocked, for any reason — including the ones we
   * deliberately don't name to the customer. Only ever used to *withhold* the
   * reassuring banner, never to explain anything, so those reasons stay
   * unnamed while the screen never claims booking works when it doesn't.
   */
  bookingBlocked: boolean;
  showMakeAPaymentButton: boolean;
  refresh: () => Promise<void>;
}

// Identifier, amount and due date on one line, so a customer with several
// invoices can tell which one to pay.
function describeBlockingInvoice(invoice: Invoice): string {
  const amount = `$${invoice.amount.toFixed(2)} ${invoice.currency.toUpperCase()}`;
  const due = formatLocalDate(asLocalDate(invoice.dueDate), "MMM D, YYYY");
  return `${invoice.invoiceIdentifier} \u00b7 ${amount} \u00b7 due ${due}`;
}

export function InvoicesCreditBanner(props: CreditBannerProps) {
  const {
    shipments,
    invoices,
    paymentTermsDays,
    creditAmount,
    creditCurrency,
    issuedInvoicesTotal,
    issuedInvoicesChargesTotal,
    invoiceWarningState,
    bookingBlocked,
    overdueIssuedInvoicesTotal,
    showMakeAPaymentButton,
    refresh,
  } = props;

  const [makePaymentsOpenCount, setMakePaymentsOpenCount] = useState(0);
  const [makePaymentsOpen, setMakePaymentsOpen] = useState(false);

  function showMakePayments() {
    // Remount key so the payment flow starts fresh each time it opens.
    setMakePaymentsOpenCount((count) => count + 1);
    setMakePaymentsOpen(true);
  }

  // Every banner below offers the same way to pay, defined once. The screen
  // used to let each branch bring its own copy, and the "Booking is not
  // blocked" one was written without one — which left a customer with money
  // owing and no way to pay it.
  const payButton = (
    <Button primary onClick={showMakePayments}>
      Make a payment
    </Button>
  );

  // The three blocked banners pass `payButton` straight through: an account
  // that can't book is told to pay, so the button has to be there even in the
  // case where listInvoices didn't hand us the invoice causing the block and
  // the dialog opens short. Everywhere else, only when the dialog has
  // something to show.
  const payActions = showMakeAPaymentButton ? payButton : undefined;

  // The flow owns its dialogs (main, confirm, receipt/failure); this screen
  // only holds the open state and the per-open remount key.
  const makePaymentsDialog = (
    <PaymentsMakePaymentsDialog
      key={makePaymentsOpenCount}
      open={makePaymentsOpen}
      onOpenChange={setMakePaymentsOpen}
      shipments={shipments}
      invoices={invoices}
      currency={creditCurrency!}
      refresh={refresh}
    />
  );

  if (overdueIssuedInvoicesTotal === undefined) {
    return <></>;
  }

  const blockingInvoices = invoices.filter(
    (invoice) => invoice.blockingBooking,
  );
  const disputedInvoices = invoices.filter(
    (invoice) => invoice.standing === InvoiceStanding.Disputed,
  );
  // Whether anything is in dispute is a question about invoices, not about money: a disputed
  // charge and a disputed refund of the same size net to zero, and reading the total made the
  // dispute explanation vanish while two disputed invoices sat on screen. disputedInvoices is the
  // list this component already built for the wording.
  const hasDisputedInvoices = disputedInvoices.length > 0;

  // The totals in this banner are sums of invoices, so they are in the invoices' currency - not in
  // the company's, which is only what the credit limit is denominated in.
  const invoicesCurrency = invoicesSingleCurrency(invoices);

  // Only worth reassuring someone whose screen could plausibly look like it's
  // blocking them. An invoice that simply isn't due yet doesn't, so this stays
  // off for the ordinary case rather than greeting everyone with a green
  // banner.
  const couldLookBlocked =
    overdueIssuedInvoicesTotal > 0 || hasDisputedInvoices;

  // The count comes from the invoices actually on screen. listInvoices drops anything without a
  // PDF key, so the wording stays plural-safe rather than claiming a count it can't see.
  function disputeClause() {
    if (disputedInvoices.length === 1) {
      return "Your invoice in dispute is on hold while we review it, and isn't counted as overdue.";
    }
    if (disputedInvoices.length > 1) {
      return `Your ${disputedInvoices.length} invoices in dispute are on hold while we review them, and aren't counted as overdue.`;
    }
    return "Invoices you've disputed are on hold while we review them, and aren't counted as overdue.";
  }

  function notBlockedExplanation() {
    if (!hasDisputedInvoices) {
      return "Nothing on your account is holding up booking right now.";
    }
    return `${disputeClause()} Nothing on your account is holding up booking right now.`;
  }

  function banner() {
    // Name the invoices actually causing the block. A bare "you have overdue
    // invoices" is what left a customer unable to tell which invoice to pay —
    // or, when the only candidate was disputed, whether they were blocked at
    // all.
    if (blockingInvoices.length > 0) {
      return (
        <Banner
          variant="danger"
          title={
            blockingInvoices.length === 1
              ? "Booking is paused until this invoice is paid"
              : "Booking is paused until these invoices are paid"
          }
          actions={payButton}
        >
          <Stack gap={1} align="start">
            {blockingInvoices.map((invoice) => (
              <T key={invoice.invoiceId} size="sm">
                {describeBlockingInvoice(invoice)}
              </T>
            ))}
          </Stack>
        </Banner>
      );
    }

    // The same block, when we can't name what's causing it. `invoices` comes
    // from listInvoices, which drops anything without a PDF key or due date
    // (customerVisible); the server's block rule doesn't, so a blocking
    // invoice can be missing from the list above. Falling through to the
    // neutral banner would tell a customer whose booking is stopped that
    // nothing is wrong — the ticket's own bug with the sign flipped.
    if (
      invoiceWarningState ===
      InvoiceWarningState.BookingSuspendedDueToOverdueInvoices
    ) {
      return (
        <Banner
          variant="danger"
          title="Booking is paused as you have overdue invoices"
          actions={payButton}
        >
          {`There are ${formatSignedPrice(overdueIssuedInvoicesTotal ?? 0)}${invoicesCurrency ? ` ${invoicesCurrency}` : ""} in overdue invoices. Paying them restores booking.`}
        </Banner>
      );
    }

    if (
      invoiceWarningState ===
      InvoiceWarningState.BookingSuspendedDueToOverCreditLimit
    ) {
      return (
        <Banner
          variant="danger"
          title="Booking suspended as you are over your credit limit"
          actions={payButton}
        >
          {invoicesGenerateCreditBannerMessage({
            paymentTermsDays,
            creditAmount,
            creditCurrency,
            // The charge total, not the net one: this banner exists because that figure went
            // past the limit, so quoting the net figure could show a number below the limit
            // directly under a title saying the customer is over it.
            issuedInvoicesTotal: issuedInvoicesChargesTotal,
            invoicesCurrency,
            overdueIssuedInvoicesTotal,
            enforcingCreditLimit: true,
          })}
        </Banner>
      );
    }

    if (!bookingBlocked && couldLookBlocked) {
      return (
        <Banner
          variant="success"
          title="Booking is not blocked"
          actions={payActions}
        >
          {notBlockedExplanation()}
        </Banner>
      );
    }

    // Also the path for a block we've decided not to name. Quoting-disabled is
    // the case that matters here, and nothing surfaces it to the customer —
    // getInvoiceWarningState ignores the flag on purpose, so the nav badge
    // stays quiet too. Saying nothing is the existing behaviour; the point of
    // bookingBlocked is that we don't say the opposite.
    return (
      <Banner
        variant="info"
        title={invoicesCreditBannerTitle(creditAmount)}
        actions={payActions}
      >
        {invoicesGenerateCreditBannerMessage({
          paymentTermsDays,
          creditAmount,
          creditCurrency,
          issuedInvoicesTotal,
          invoicesCurrency,
        })}
      </Banner>
    );
  }

  return (
    <>
      {makePaymentsDialog}
      {banner()}
    </>
  );
}
